import { PayloadAction } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { SagaIterator } from "redux-saga";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { setGlobalUserInfo, setToast } from "scenes/redux";
import { selectUserCidpId, selectUserInfo } from "scenes/selectors";
import { APIError, APIResponseError } from "types/ApiErrorResponse";
import factory from "utils/getApiHandlerEffect";
import {
  afterSubmitUsernameUpdate,
  afterSubmitUsernameUpdateFailure,
  submitUsernameUpdate,
} from "./redux";

export const isDuplicateUsernameUpdateError = (error: APIError): boolean => {
  if (error.body) {
    try {
      const parsed: APIResponseError = JSON.parse(error.body);
      const message = parsed.errors?.[0]?.message ?? parsed.message;

      return (
        parsed.status === 409 &&
        message?.toLowerCase() === "this username is already in use"
      );
    } catch (e) {
      return false;
    }
  }

  return false;
};

function* onSubmitUsernameUpdate(
  action: PayloadAction<{ username: string }>
): SagaIterator {
  const { username } = action.payload;
  const cidpId = yield select(selectUserCidpId);
  const userInfo = yield select(selectUserInfo);
  const apiHandler: APIHandler = yield call(() => factory());

  if (!cidpId) {
    yield put(afterSubmitUsernameUpdateFailure(null));
    return;
  }

  try {
    yield call(() => apiHandler.updateUsername(cidpId, { username }));
    yield put(afterSubmitUsernameUpdate());
    yield put(
      setGlobalUserInfo({
        ...userInfo,
        preferred_username: username,
      })
    );
    yield put(
      setToast({
        message: "Username Updated.",
        messageType: "positive",
        label: "Success",
      })
    );
    yield put(push("/dashboard/securitysettings"));
  } catch (e: unknown) {
    const isDuplicate = isDuplicateUsernameUpdateError(e as APIError);
    yield put(
      afterSubmitUsernameUpdateFailure(isDuplicate ? "DUPLICATE" : null)
    );
    if (isDuplicate) {
      return;
    }
    yield put(
      setToast({
        message: "Username could not be updated.",
        messageType: "error",
        label: "Something went wrong.",
      })
    );
  }
}

// watcher function
export default function* editUsernameSaga() {
  yield takeLatest(submitUsernameUpdate.type, onSubmitUsernameUpdate);
}
